import { AppstoreAddOutlined } from "@ant-design/icons"
import { Button, Typography } from "antd"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { neutral } from "../../../../components/widgets/tokens.ts"
import { useElementSize } from "../../../Calendar/CalendarPage/hooks/use-element-size.ts"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import {
  setReportFilters,
  usedSpecKeys
} from "../../helpers/reportDefinition.ts"
import { useReportBuildActions } from "../../hooks/useReportBuildActions.ts"
import { reportCanvasApplyDefinition } from "../../redux/reportCanvas.slice.ts"
import type { ReportWidgetFilters } from "../../types/canvas.types.ts"
import { ReportCanvas } from "../canvas/ReportCanvas.tsx"
import { ReportFilterBar } from "../canvas/ReportFilterBar.tsx"
import { ReportFiltersSummary } from "../filters/ReportFiltersSummary.tsx"
import { ReportAddWidgetPanel } from "./ReportAddWidgetPanel.tsx"
import { ReportWidgetSettingsPanel } from "./ReportWidgetSettingsPanel.tsx"

const { Text, Title } = Typography

interface ReportBuildLayoutProps {
  /**
   * `summary` in the create wizard, whose Filters step owns the report's
   * default filters; `editable` on the edit page, which has no such step.
   */
  reportFilters?: "summary" | "editable"
  /** The report's name over the canvas, when the page has no header of its own. */
  title?: string
}

/**
 * Catalog on the left, the report canvas in the middle, and the selected
 * widget's settings on the right — that panel exists only while a widget is
 * selected, so closing it gives the canvas its width back.
 */
const COMPACT_BELOW = 1100

const overlayStyle: React.CSSProperties = {
  position: "absolute",
  top: 0,
  bottom: 0,
  zIndex: 5,
  boxShadow: "0 8px 24px rgba(15,23,42,.16)"
}

export const ReportBuildLayout = ({
  reportFilters = "editable",
  title
}: ReportBuildLayoutProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const definition = useAppSelector((s) => s.reportCanvas.definition)
  const catalog = useAppSelector((s) => s.reportCanvas.catalog)
  const catalogStatus = useAppSelector((s) => s.reportCanvas.catalogStatus)
  const { addWidget, selectedWidget } = useReportBuildActions()

  const addedSpecKeys = useMemo(() => usedSpecKeys(definition), [definition])

  // Below this width the catalog and the settings would squeeze the canvas,
  // so both become panels over it: the catalog on demand, the settings while
  // a widget is selected.
  const { ref: frameRef, size } = useElementSize({ width: 1400, height: 0 })
  const compact = size.width < COMPACT_BELOW
  const [catalogOpen, setCatalogOpen] = useState(false)

  const handleReportFiltersChange = (filters: ReportWidgetFilters) => {
    if (!definition) return
    dispatch(reportCanvasApplyDefinition(setReportFilters(definition, filters)))
  }

  const catalogPanel = (
    <ReportAddWidgetPanel
      catalog={catalog}
      loading={catalogStatus === "loading"}
      failed={catalogStatus === "error"}
      onAdd={addWidget}
      addedSpecKeys={addedSpecKeys}
    />
  )

  return (
    <div ref={frameRef} className="relative flex h-full min-h-0 gap-4">
      {!compact && catalogPanel}
      {compact && catalogOpen && (
        <div style={{ ...overlayStyle, left: 0 }} className="flex rounded-xl">
          {catalogPanel}
        </div>
      )}

      <section
        aria-label={t("reports.build.canvasLabel")}
        className="flex min-w-0 flex-1 flex-col gap-3 overflow-auto rounded-xl border border-solid p-4"
        style={{ borderColor: neutral.border, background: neutral.surface }}
      >
        <div className="flex items-center justify-between gap-3">
          {title ? (
            <Title
              level={4}
              className="m-0 min-w-0 text-start"
              ellipsis={{ tooltip: title }}
            >
              {title}
            </Title>
          ) : (
            <span />
          )}
          {compact && (
            <Button
              icon={<AppstoreAddOutlined aria-hidden />}
              aria-expanded={catalogOpen}
              onClick={() => setCatalogOpen((open) => !open)}
            >
              {catalogOpen
                ? t("reports.build.hideCatalog")
                : t("reports.build.addWidget")}
            </Button>
          )}
        </div>
        {reportFilters === "editable" ? (
          <>
            <Text type="secondary" className="text-xs font-semibold">
              {t("reports.build.globalFilters")}
            </Text>
            <ReportFilterBar
              filters={definition?.filters ?? {}}
              onChange={handleReportFiltersChange}
            />
          </>
        ) : (
          <ReportFiltersSummary definition={definition} compact />
        )}

        <ReportCanvas
          selectable
          hidePanels
          emptyHint={t("reports.build.emptyCanvasHint")}
        />
      </section>

      {selectedWidget &&
        (compact ? (
          <div style={{ ...overlayStyle, right: 0 }} className="flex rounded-xl">
            <ReportWidgetSettingsPanel />
          </div>
        ) : (
          <ReportWidgetSettingsPanel />
        ))}
    </div>
  )
}
